import { Surface } from '@nexvia-logistics/ui';
import { ShieldCheckIcon } from '@/shared/icons';
import { DocumentUploadRow } from './DocumentUploadRow';
import { DOCUMENTS_SECTION_ID, STORAGE_DOCUMENT_TYPES } from './storage-documents.constants';
import type { StorageDocument } from './storage-api';

export interface DocumentsCardProps {
  documents: StorageDocument[];
  onChanged: () => void;
}

/**
 * Always renders the three fixed document types, in the same order, whether or not the account
 * has uploaded each one yet — `documents` only ever contains the ones that exist, so a missing
 * type must still show as "not uploaded" rather than not show at all.
 *
 * Title is "Documentación" (not "Documentos" — Claude Design mockup fix 4a). Header icon reuses
 * `ShieldCheckIcon` (`src/shared/icons.tsx` — that file is out of scope to edit this pass, a
 * sibling agent owns it concurrently for the shared shell): `SealCheckIcon` is this same page's own
 * nav icon already, so using it again here would read as a confusing duplicate of the page's own
 * nav glyph inside its own content; `ShieldCheckIcon` ("protected/compliant documentation") is both
 * a close semantic fit for three compliance documents and not reused anywhere else on this screen.
 */
export function DocumentsCard({ documents, onChanged }: DocumentsCardProps) {
  return (
    <Surface as="section" variant="outlined" padding="md">
      {/* NEX-1164 — the approval banner's upload CTA links here (`Surface` takes no `id`). */}
      <h2 id={DOCUMENTS_SECTION_ID} className="flex scroll-mt-24 items-center gap-2 text-ui-h3 text-on-surface">
        <ShieldCheckIcon className="shrink-0 text-primary" />
        Documentación
      </h2>
      <div className="mt-4 flex flex-col gap-3">
        {STORAGE_DOCUMENT_TYPES.map((documentType) => (
          <DocumentUploadRow
            key={documentType}
            documentType={documentType}
            document={documents.find((doc) => doc.documentType === documentType)}
            onChanged={onChanged}
          />
        ))}
      </div>
    </Surface>
  );
}
